/**
 * The Contacts page's URL state.
 *
 * The filtered list IS the URL: a colleague can be sent "everyone tagged crew whose name
 * contains 'lie'" as a link, and a reload lands on the same list rather than on the unfiltered
 * one. That is why the search box, the tag filter and the SORT write to the address bar instead
 * of to a `useState` — and why this module, not a component, decides what a valid Contacts URL is.
 *
 * Absent means default. `?tab=contacts`, `?q=`, `?sort=name` and `?dir=asc` are never written, so
 * the plain `/contacts` a person types is the same location the menu links to and the router does
 * not treat them as two.
 */
import {
  type ContactCompanySortKey,
  type ContactSortKey,
  type OrganizationMemberSortKey,
  type OrganizationMemberStatus,
  type OrgRole,
  type SortDirection,
  ORG_ROLES,
} from '@tvitvi/shared';

import type { TableSort } from '@/components/ui/Table';

export const CONTACTS_TABS = ['contacts', 'organization', 'companies'] as const;
export type ContactsTab = (typeof CONTACTS_TABS)[number];

export const DEFAULT_TAB: ContactsTab = 'contacts';

/**
 * The sorts each tab OFFERS, mapped to the table column that carries them.
 *
 * `satisfies Partial<Record<…SortKey, string>>` is the load-bearing part: a key the endpoint does
 * not accept cannot be written here, so the page can never ask for a sort the API would reject —
 * and a contract that dropped one would fail to compile rather than 400 at runtime.
 *
 * The only sort affordance in this product is a column header — `Table` owns that mechanism and
 * there is deliberately not a second one. `created` waited for a column until bølge 3 gave it
 * «Opprettet»; a list ordered by a value the reader cannot see has stopped explaining itself.
 */
/*
 * `company` ER TILBAKE (A2, 22. oktober): «sorterbar på kolonnen Organisasjon». Sorteringen går på
 * firmaet (`ix_contacts__org_company_name`); en rad uten firma, som en kollega der cellen viser
 * organisasjonen hen er med i, kommer først. Det var grunnen til at den ble tatt bort 22. september.
 */
export const CONTACT_SORT_COLUMNS = {
  name: 'displayName',
  company: 'organizations',
  created: 'createdAt',
} as const satisfies Partial<Record<ContactSortKey, string>>;

export const MEMBER_SORT_COLUMNS = {
  name: 'displayName',
  role: 'role',
} as const satisfies Partial<Record<OrganizationMemberSortKey, string>>;

/**
 * The count column is not sortable — see `COMPANY_SORTS` in the API for why counting the whole
 * register to order it is the thing keyset pagination exists to avoid.
 */
export const COMPANY_SORT_COLUMNS = {
  name: 'name',
  created: 'createdAt',
} as const satisfies Partial<Record<ContactCompanySortKey, string>>;

export type ContactSortColumn = keyof typeof CONTACT_SORT_COLUMNS;
export type MemberSortColumn = keyof typeof MEMBER_SORT_COLUMNS;
export type CompanySortColumn = keyof typeof COMPANY_SORT_COLUMNS;

const SORT_COLUMNS = {
  contacts: CONTACT_SORT_COLUMNS,
  organization: MEMBER_SORT_COLUMNS,
  companies: COMPANY_SORT_COLUMNS,
} as const satisfies Record<ContactsTab, Readonly<Record<string, string>>>;

/**
 * The sort a URL with no `?sort=` means, and the default both endpoints apply themselves.
 *
 * Typed as the intersection so it cannot drift: if either contract stopped offering `name`, this
 * line would stop compiling instead of quietly sending a sort one of the two lists rejects.
 */
const DEFAULT_SORT: ContactSortKey & OrganizationMemberSortKey & ContactCompanySortKey = 'name';
const DEFAULT_DIRECTION: SortDirection = 'asc';

/**
 * What `validateSearch` produces and what `navigate({ search })` must be handed back.
 *
 * `tags` is one comma-separated parameter rather than a repeated one because that is the shorter
 * URL and the API's `uuidListQuerySchema` accepts exactly this form — the link a person copies
 * out of the address bar is the query the server receives.
 *
 * `sort` and `dir` are the two halves of one order, kept as two parameters because that is the
 * shape the API takes: a link should not need translating on the way in.
 */
export interface ContactsSearch {
  tab?: ContactsTab;
  q?: string;
  tags?: string;
  /** ONE type, because a contact has exactly one (vedtak 57.1). Never a list. */
  type?: string;
  sort?: string;
  dir?: SortDirection;
  /**
   * «Deaktiverte» på Organisasjon-fana (QA15 blokk B2). Bare den ene verdien finnes i URL-en:
   * «Aktive» er standarden, og standarden skrives aldri — samme regel som `tab` og `sort`.
   */
  status?: OrganizationMemberStatus;
  /** F53: filter på rolle i medlemslista. Bare på Organisasjon-fana; fravær er «Alle roller». */
  role?: OrgRole;
  /** 253.69: bare plattformadminene. Bare på Organisasjon-fana, og bare `true` skrives. */
  admins?: true;
  /**
   * 253.16: «Mine kontakter» — de personlige koblingene mine. Fravær er «Organisasjonens kontakter».
   * Bare på kontaktlista; medlemmene (`tab=organization`) har sin egen visning.
   */
  view?: 'mine';
  /** 253.19: filteret Firma — én rad i firmaregisteret. Bare på organisasjonens kontakter. */
  company?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MAX_QUERY_LENGTH = 120;
const MAX_TAG_IDS = 20;

/**
 * A search value that came off the URL, as a string — whatever JSON thought it was.
 *
 * TANSTACK ROUTER `JSON.parse`s EVERY SEARCH VALUE BEFORE `validateSearch` SEES IT. So `?q=Nora`
 * arrives as the string it looks like, and `?q=90011222` arrives as the NUMBER 90011222. This
 * function used to return `undefined` for anything that was not already a string, which meant a
 * search for a phone number — one of the four things this register explicitly searches by, and
 * the reason the search was widened past names in the first place — silently rendered the
 * unfiltered list with an empty search box. Same for `?q=2024` and `?q=112`.
 *
 * `features/inbox/search-params.ts` documents this mechanism at length and handles it for its own
 * boolean. It was fixed there, where it was reported, and not here. That is the failure this
 * repository keeps repeating, so: the rule is that a search value is TEXT, and the parse is
 * undone rather than the type being refused.
 *
 * Links the app writes itself never hit it — the router re-encodes an ambiguous string so it
 * round-trips — so this only ever bit somebody who typed or pasted a URL, or followed an old one.
 * Which is exactly who a search parameter in the address bar is for.
 */
function trimmedString(value: unknown, max: number): string | undefined {
  const raw =
    typeof value === 'string'
      ? value
      : typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : undefined;
  if (raw === undefined) return undefined;
  const text = raw.trim().slice(0, max);
  return text.length > 0 ? text : undefined;
}

/**
 * Validated at the edge of the router, like `safeRedirect` is: no component downstream ever sees
 * a `tab` that is not one of the three, a query longer than the API would accept, or a sort the
 * tab in front of the reader cannot draw.
 *
 * The sort is validated AGAINST THE TAB, because the three lists are three endpoints with three
 * sets of keys. `?tab=organization&sort=company` is not a member sort, so it is dropped here and
 * that list comes back in its default order — rather than travelling to an API that would 400.
 */
export function validateContactsSearch(raw: Record<string, unknown>): ContactsSearch {
  const search: ContactsSearch = {};

  /*
   * 253.19: «Eksterne organisasjoner» er ikke en fane lenger — Firma er et felt og et filter, og
   * firmasiden nås fra navnet. Firmaregisteret står fortsatt på `?tab=companies`, nådd fra
   * filteret Firma («Alle firma»), for å lage et firma, koble det og eksportere lista.
   */
  const tab = raw['tab'];
  if (typeof tab === 'string' && (CONTACTS_TABS as readonly string[]).includes(tab)) {
    if (tab !== DEFAULT_TAB) search.tab = tab as ContactsTab;
  }

  const q = trimmedString(raw['q'], MAX_QUERY_LENGTH);
  if (q !== undefined) search.q = q;

  const tags = parseTagIds(raw['tags']);
  if (tags.length > 0) search.tags = tags.join(',');

  const type = trimmedString(raw['type'], MAX_QUERY_LENGTH);
  if (type !== undefined) search.type = type;

  const offered: readonly string[] = Object.keys(SORT_COLUMNS[tabOf(search)]);
  const sort = raw['sort'];
  if (typeof sort === 'string' && offered.includes(sort) && sort !== DEFAULT_SORT) {
    search.sort = sort;
  }
  if (raw['dir'] === 'desc') search.dir = 'desc';

  /*
   * Bare på den fana som HAR filteret. En `?status=deactivated` som blir hengende igjen når noen
   * bytter til Kontakter, ville vært et filter uten en kontroll å skru av — akkurat den formen
   * `sort` over allerede avviser ved å sjekke mot fanas egne kolonner.
   */
  if (raw['status'] === 'deactivated' && tabOf(search) === 'organization') {
    search.status = 'deactivated';
  }
  const role = raw['role'];
  if (
    typeof role === 'string' &&
    (ORG_ROLES as readonly string[]).includes(role) &&
    tabOf(search) === 'organization'
  ) {
    search.role = role as OrgRole;
  }
  if ((raw['admins'] === true || raw['admins'] === 'true') && tabOf(search) === 'organization') {
    search.admins = true;
  }
  if (raw['view'] === 'mine' && tabOf(search) === 'contacts') search.view = 'mine';
  const company = raw['company'];
  if (
    typeof company === 'string' &&
    UUID.test(company) &&
    tabOf(search) === 'contacts' &&
    search.view === undefined
  ) {
    search.company = company;
  }

  return search;
}

/** F53: rollen lista er filtrert på, eller undefined for alle. */
export function memberRoleOf(search: ContactsSearch): OrgRole | undefined {
  return search.role;
}

/** «Aktive» med mindre URL-en sier noe annet. */
export function memberStatusOf(search: ContactsSearch): OrganizationMemberStatus {
  return search.status ?? 'active';
}

/** Accepts the comma form and the array form; returns ids only, de-duplicated and capped. */
function parseTagIds(value: unknown): readonly string[] {
  const parts = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : ([] as unknown[]);
  const ids = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);
  return [...new Set(ids)].slice(0, MAX_TAG_IDS);
}

export function tabOf(search: ContactsSearch): ContactsTab {
  return search.tab ?? DEFAULT_TAB;
}

export function tagIdsOf(search: ContactsSearch): readonly string[] {
  return parseTagIds(search.tags);
}

export function queryOf(search: ContactsSearch): string {
  return search.q ?? '';
}

function keyIn<TKey extends string>(
  columns: Readonly<Record<TKey, string>>,
  value: string | undefined,
): TKey {
  return value !== undefined && value in columns ? (value as TKey) : (DEFAULT_SORT as TKey);
}

/** The order `GET /v1/contacts` is asked for. Always a key the endpoint accepts. */
export function contactSortOf(search: ContactsSearch): {
  sort: ContactSortColumn;
  direction: SortDirection;
} {
  return {
    sort: keyIn(CONTACT_SORT_COLUMNS, search.sort),
    direction: search.dir ?? DEFAULT_DIRECTION,
  };
}

/** The order `GET /v1/contact-companies` is asked for. */
export function companySortOf(search: ContactsSearch): {
  sort: CompanySortColumn;
  direction: SortDirection;
} {
  return {
    sort: keyIn(COMPANY_SORT_COLUMNS, search.sort),
    direction: search.dir ?? DEFAULT_DIRECTION,
  };
}

/** The order `GET /v1/organization/members` is asked for. */
export function memberSortOf(search: ContactsSearch): {
  sort: MemberSortColumn;
  direction: SortDirection;
} {
  return {
    sort: keyIn(MEMBER_SORT_COLUMNS, search.sort),
    direction: search.dir ?? DEFAULT_DIRECTION,
  };
}

/** What `Table` is handed, so the sorted column wears `aria-sort` and the arrow. */
export function tableSortOf<TKey extends string>(
  columns: Readonly<Record<TKey, string>>,
  order: { sort: TKey; direction: SortDirection },
): TableSort {
  return { columnId: columns[order.sort], direction: order.direction };
}

/**
 * The next URL for a header click — and the one place that guarantees a sort change starts the
 * list again from the top.
 *
 * A keyset cursor is minted for one sort spec and `decodeCursor` refuses it under any other
 * (`packages/shared/src/http/pagination.ts`), so carrying a position across a sort change is not
 * a stale-data problem, it is a 400. Nothing is carried: what comes back holds the tab and the
 * filters, and a position is not one of them. Should this page ever grow a `?after=` cursor, this
 * is the function that has to drop it.
 */
export function withSort(search: ContactsSearch, sort: TableSort): ContactsSearch {
  const columns: Readonly<Record<string, string>> = SORT_COLUMNS[tabOf(search)];
  const key = Object.keys(columns).find((candidate) => columns[candidate] === sort.columnId);
  if (key === undefined) return search;

  const next: ContactsSearch = { ...search };
  if (key === DEFAULT_SORT) delete next.sort;
  else next.sort = key;
  if (sort.direction === DEFAULT_DIRECTION) delete next.dir;
  else next.dir = sort.direction;
  return next;
}

/** True when the list the user is looking at has been narrowed — which changes the empty copy. */
export function isFiltered(search: ContactsSearch): boolean {
  return (
    queryOf(search).length > 0 ||
    tagIdsOf(search).length > 0 ||
    search.type !== undefined ||
    search.company !== undefined
  );
}
